import { LOGGER_LOG_TYPE } from 'Config';
import { UserProfile } from 'api/account/models/UserProfile';
import StoppingAreasService from 'api/stoppingAreas/StoppingAreasService';
import { StoppingAreaDto } from 'api/stoppingAreas/models/StoppingAreaDto';
import UsersService from 'api/users/UsersService';
import ScreenTitle from 'common/components/screenTitle/ScreenTitle';
import Loading from 'common/services/Loading';
import Logger from 'common/services/Logger';
import { useEffect, useState } from 'react';
import { Breadcrumb, Button, Col, Form, Row } from 'react-bootstrap';
import styles from './StoppingAreaScreen.module.scss';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { Reducers } from 'store/types';
import { Circle, MapContainer, TileLayer } from 'react-leaflet';
import DrawTools, { LocationArea } from 'common/components/maps/DrawTools';
import QuestionYesNo from 'common/components/questionYesNo/QuestionYesNo';
import ZoomLevelControl from 'common/components/maps/ZoomLevelControl';

type Props = {
};

const StoppingAreaScreen: React.FC<Props> = () => {
    const { id, type } = useParams<{ id: string, type: string }>();
    const [area, setArea] = useState<LocationArea>();
    const [zoomDefault, setZoomDefault] = useState<number>(12);
    const [isDetails, setIsDetails] = useState<boolean>(type === 'details');
    const userProfile = useSelector<Reducers, UserProfile | null>(state => state.authentication.profile);
    const hasStoppingAreasWritePolicy = UsersService.hasPolicies(userProfile?.policies || [], ['STOPPING_AREAS_WRITE']);
    const [refresh, setRefresh] = useState<number>(1);
    const [position, setPosition] = useState<GeolocationPosition>();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [validated, setValidated] = useState(false);
    const [showRemoveModal, setShowRemoveModal] = useState<boolean>(false);
    const [itemToRemove, setItemToRemove] = useState<StoppingAreaDto | null>(null);

    const initialStatus: StoppingAreaDto = {
        id: '',
        name: '',
        zoom: zoomDefault,
        latitude: 0,
        longitude: 0,
        radius: 0,
        rowVersion: ''
    };
    const [formData, setFormData] = useState(initialStatus);
    const [item, setItem] = useState<StoppingAreaDto>(initialStatus);

    const getData = async () => {
        try {
            let result: any = initialStatus;

            if (id) {
                Loading.show();
                result = await StoppingAreasService.getById(id);
                Loading.hide();
            }
            setItem(result);
            setFormData(result);
            setZoomDefault(result.zoom ?? zoomDefault);
            if (result && result.radius && result.latitude && result.longitude) {
                const area: LocationArea = { latitude: result.latitude, longitude: result.longitude, mRadius: result.radius, radius: result.radius };
                setArea(area);
            }
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t get alarm setting information', error);
            toast.error(t('messages.error_load_info'));
        } finally {
            setRefresh(refresh + 1);
        }
    };

    const setCurrentPosition = (position: GeolocationPosition) => {
        try {
            setPosition(position);
            setRefresh(refresh + 1);
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t get device model list', error);
            toast.error(t('messages.error_load_info'));
        } finally {
            Loading.hide();
        }
    }

    const clearArea = () => {
        setArea(undefined);
        setFormData({ ...formData, latitude: 0, longitude: 0, radius: 0 });
        setRefresh(refresh + 1);
    }

    useEffect(() => {
        if (type === 'new') {
            Loading.show();
            navigator.geolocation.getCurrentPosition((position: GeolocationPosition) => setCurrentPosition(position), () => Loading.hide());
        }
    }, [type]);

    useEffect(() => {
        getData().catch(console.error);
    }, [id, type]);

    function handleChange(e: any) {
        const key = e.target.name;
        const value = e.target.value;
        setFormData({ ...formData, [key]: value });
    }

    const navigateTo = (typeUrl?: string, id?: string) => {
        if (typeUrl) {
            navigate(`/stoppingarea/${typeUrl}/${id}`);
            setIsDetails(typeUrl === 'details');
        } else {
            navigate('/stoppingareas');
        }
    }

    const onCancelRemove = () => {
        setItemToRemove(null);
        setShowRemoveModal(false);
    };

    const onRemove = async () => {
        if (itemToRemove === null) {
            toast.error(t('messages.record_delete_error'));
            return;
        }
        try {
            await StoppingAreasService.remove(itemToRemove);
            onCancelRemove();
            navigateTo();
            toast.success(t('messages.record_delete_success'));
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t delete stopping areas', error);
            toast.error(t('messages.record_delete_error'));
        }
    };

    const showRemoveItemDialog = async (item: StoppingAreaDto) => {
        setItemToRemove(item);
        setShowRemoveModal(true);
    }

    const onSubmit = async (event: any) => {
        const form = event.currentTarget;
        event.preventDefault();
        event.stopPropagation();
        setValidated(true);
        if (form.checkValidity() === false) {
            return;
        }
        try {
            Loading.show();
            const model: StoppingAreaDto = {
                id: formData.id,
                name: formData.name,
                zoom: zoomDefault,
                latitude: formData.latitude ?? 0,
                longitude: formData.longitude ?? 0,
                radius: formData.radius ?? 0,
                rowVersion: formData.rowVersion
            };

            if (model && model.id) {
                await StoppingAreasService.update(model)
                navigateTo('details', id);
            } else if (model) {
                const id = await StoppingAreasService.create(model)
                navigateTo('details', id);
            }
            Loading.hide();
            toast.success(t('messages.record_save_success'));
        } catch (error: any) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, `Couldn't update alarm setting with id: ${form.id}`, error);
            toast.error(t('messages.record_save_error'));
            Loading.hide();
        }
    };

    useEffect(() => {
        if (area) {
            setFormData({ ...formData, latitude: area.latitude, longitude: area.longitude, radius: area.mRadius });
        } else {
            setFormData({ ...formData, latitude: 0, longitude: 0, radius: 0 });
        }
    }, [area]);

    const onCreateArea = (area: LocationArea) => {
        setArea(area);
    }

    const renderMap = () => {
        return <>
            <MapContainer center={[area ? area.latitude : position?.coords?.latitude ?? 52.516316, area ? area.longitude : position?.coords?.longitude ?? 13.377825]} maxZoom={18} minZoom={6} zoom={zoomDefault} scrollWheelZoom={true} className={styles.map}>
            {zoomDefault && <ZoomLevelControl onZoomChange={setZoomDefault} level={zoomDefault} />}
                <TileLayer
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                {area ?
                    <Circle center={[area.latitude, area.longitude]} radius={area.mRadius}></Circle> :
                    (isDetails ? <></> : <DrawTools onCreated={onCreateArea} />)
                }
            </MapContainer></>
    }

    const onChangeCoordinates = () => {
        if (formData.latitude && formData.longitude) {
            const p: GeolocationPosition = {
                coords: {
                    latitude: formData.latitude,
                    longitude: formData.longitude,
                    accuracy: 0,
                    altitude: null,
                    altitudeAccuracy: null,
                    heading: null,
                    speed: null
                },
                timestamp: Date.now()
            };
            setCurrentPosition(p);
            setZoomDefault(zoomDefault);
            if (formData.radius) {
                setArea({ latitude: formData.latitude, longitude: formData.longitude, mRadius: formData.radius, radius: formData.radius });
            }
        }
    }

    return (
        <ScreenTitle title={t('stopping_areas.title')}>
            <Breadcrumb>
                <Breadcrumb.Item onClick={() => { navigateTo() }}>
                    {t('stopping_areas.title')}
                </Breadcrumb.Item>
                <Breadcrumb.Item active>{t(`common.${type}`)}</Breadcrumb.Item>
            </Breadcrumb>

            <div className="bg-white rounded-2xl border shadow-sm p-4" style={{ borderColor: '#e2e8f0' }}>
            <Form onSubmit={onSubmit} noValidate validated={validated}>
                <Col xs={12} key={item.name}>
                    <Form.Group className="mb-3" controlId={'name'}>
                        <Form.Label>{t('stopping_areas.name')}{!isDetails ? '*' : ''}</Form.Label>
                        <Form.Control required={true} name={'name'} type="text" maxLength={250} defaultValue={item.name} readOnly={isDetails} onChange={handleChange} placeholder={t('stopping_areas.name')} />
                    </Form.Group>
                </Col>
                <Col xs={12} md={3}>
                    <Form.Group className="mb-3" controlId={'latitude'}>
                        <Form.Label>{t('stopping_areas.latitude')}{!isDetails ? '*' : ''}</Form.Label>
                        <Form.Control required={true} name={'latitude'}
                            value={formData.latitude} type="text" readOnly={isDetails}
                            onChange={handleChange}
                            onBlur={onChangeCoordinates} placeholder={t('stopping_areas.latitude')} />
                    </Form.Group>
                </Col>
                <Col xs={12} md={3}>
                    <Form.Group className="mb-3" controlId={'longitude'}>
                        <Form.Label>{t('stopping_areas.longitude')}{!isDetails ? '*' : ''}</Form.Label>
                        <Form.Control required={true} name={'longitude'}
                            value={formData.longitude} type="text" readOnly={isDetails}
                            onChange={handleChange}
                            onBlur={onChangeCoordinates} placeholder={t('stopping_areas.longitude')} />
                    </Form.Group>
                </Col>
                <Col xs={12} md={2}>
                    <Form.Group className="mb-3" controlId={'radius'}>
                        <Form.Label>{t('stopping_areas.radius')}(m){!isDetails ? '*' : ''}</Form.Label>
                        <Form.Control required={true} name={'radius'} value={formData.radius} type="number" step={0.01} readOnly={isDetails} onChange={handleChange} onBlur={onChangeCoordinates} placeholder={t('stopping_areas.radius')} />
                    </Form.Group>
                </Col>
                {!isDetails && <Col xs={12} md={2} key={'btns'}>
                        <div className={styles.smallButtonsContainer}>
                            <Button variant="outline-danger" size="sm" type="button" className={styles.button} onClick={clearArea}>
                                {t('common.clear')}
                            </Button>
                        </div>
                </Col>}
                <Row key={`map_${refresh}`}>
                        {renderMap()}
                </Row>
                <div className={styles.buttonsContainer}>
                    <Button variant="secondary" type="button" className={styles.button} onClick={() => navigateTo()}>
                        {t('common.cancel')}
                    </Button>
                    {isDetails && hasStoppingAreasWritePolicy &&
                        <Button variant="danger" type="button" className={styles.button} onClick={() => showRemoveItemDialog(item)} >
                            {t('common.delete')}
                        </Button>
                    }
                    {isDetails && hasStoppingAreasWritePolicy &&
                        <Button variant="warning" type="button" className={styles.button} onClick={() => { navigateTo('edit', item?.id); }}>
                            {t('common.edit')}
                        </Button>
                    }
                    {!isDetails && hasStoppingAreasWritePolicy &&
                        <Button variant="primary" type="submit" className={styles.button}>
                            {t('common.save')}
                        </Button>
                    }
                </div>
            </Form>
            </div>
            <QuestionYesNo onNo={onCancelRemove} onYes={onRemove} isVisible={showRemoveModal} message={t('messages.remove_record_with_ident', { name: item?.name ?? '' })} />
        </ScreenTitle>
    );
};

export default StoppingAreaScreen;
